import { type Href, useRouter } from 'expo-router';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import TagGroupSelect from '@/components/Custom/TagGroupSelect';
import Text from '@/components/Unistyles/Text';
import TextInput from '@/components/Unistyles/TextInput';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';
import type { WelcomeFormValues } from '@/layouts/Welcome/form';
import { MAXIMUM_EVENT_TITLE_LENGTH } from '@/utils/constants';

type WelcomeOrgManagerProps = {
  nextRoute?: Href;
};

export default function WelcomeOrgManager({
  nextRoute = '/welcome/org/league-season',
}: WelcomeOrgManagerProps) {
  const { navigate } = useRouter();
  const { control, setValue } = useFormContext<WelcomeFormValues>();
  const title = useWatch({ control, name: 'organizationTitle' });
  const tagGroupId = useWatch({ control, name: 'organizationTagGroupId' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isValid = title.trim().length > 0 && Boolean(tagGroupId);

  const onPressContinue = () => {
    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      setErrorMessage('Organization name is required.');
      return;
    }

    if (!tagGroupId) {
      setErrorMessage('Sport is required.');
      return;
    }

    navigate(nextRoute);
  };

  return (
    <WelcomeStepFrame
      title="Create your organization"
      subtitle="Start with the umbrella your leagues, seasons, and teams will live under."
      footer={
        <WelcomeActions>
          <WelcomePrimaryAction
            label="Continue"
            disabled={!isValid}
            onPress={onPressContinue}
          />
        </WelcomeActions>
      }
    >
      <WelcomeStepScrollContent>
        <WelcomeCard>
          <TextInput
            autoCapitalize="words"
            enterKeyHint="done"
            maxLength={MAXIMUM_EVENT_TITLE_LENGTH}
            placeholder="Organization name"
            value={title}
            onChangeText={(value) => {
              setValue('organizationTitle', value, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          />
          <TagGroupSelect
            selected={tagGroupId ? [tagGroupId] : []}
            onPress={(pressedTagGroupId) => {
              setValue(
                'organizationTagGroupId',
                tagGroupId === pressedTagGroupId ? null : pressedTagGroupId,
                {
                  shouldDirty: true,
                  shouldValidate: true,
                },
              );
            }}
          />
          {errorMessage ? (
            <Text variant="error" center selectable>
              {errorMessage}
            </Text>
          ) : null}
        </WelcomeCard>
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}
